/**
 * Shared magic-number constants for the query layer.
 *
 * Any numeric limit, page size, or cap used inside `src/lib/queries/*` lives
 * here with a named export so call sites read as intent, not as a literal.
 * Add a new constant the first time a number would otherwise be inlined.
 *
 * Example:
 *   import { TITLE_GEN_MAX_CHARS } from './_constants'
 *   assistantMessage?.substring(0, TITLE_GEN_MAX_CHARS)
 */

/** Max assistant-message characters sent to the title-gen edge function. */
export const TITLE_GEN_MAX_CHARS = 300

/** Default page size for user-scoped prayer journal lists. */
export const PRAYER_PAGE_SIZE = 100

/** Row cap on the admin message-feedback panel (newest-first). */
export const ADMIN_FEEDBACK_LIMIT = 300

/** Row cap on the admin Premium-signups list (newest-first by premium_since). */
export const ADMIN_PREMIUM_SIGNUPS_LIMIT = 300

/** Default lookback window (days) for daily digest history. */
export const ADMIN_DIGEST_DAYS = 7

/** Default row cap on admin prompt-tuning + content-gap report listings. */
export const ADMIN_REPORT_LIMIT = 20

/** Default page size for fetchChatAnalytics. */
export const ADMIN_CHAT_ANALYTICS_LIMIT = 500

/** Default page size for fetchContentPosts. */
export const ADMIN_CONTENT_POSTS_LIMIT = 100

/** Default page size for fetchInviteCodes. */
export const ADMIN_INVITE_CODES_LIMIT = 200

/** Default page size for fetchSupportRequests. */
export const ADMIN_SUPPORT_REQUESTS_LIMIT = 100

/** Max rows returned by fetchPeople when no category/testament filter applied. */
export const PEOPLE_BROWSE_LIMIT = 500

/** Min substring length before searchPeople hits the DB (avoids 1-char floods). */
export const PEOPLE_SEARCH_MIN_CHARS = 2

/** Default lookback window (days) for the per-slug clicks-over-time chart. */
export const LINK_CLICKS_DEFAULT_DAYS = 30

/** Slug used by the bio-link "latest video" swap button on the admin page. */
export const LATEST_VIDEO_SLUG = 'latest-video'

/**
 * AI Insight lens ids — one per chip in BibleInsightPanel. The edge function
 * maps these to per-lens prompt templates; the value is also stored as
 * `bible_notes.insight_lens` when a lens response is saved to Notes.
 */
export const INSIGHT_LENSES = {
  QUICK: 'quick',
  HISTORICAL: 'historical',
  // GREEK lens removed 2026-04-30 — folded into the new verse-level Original
  // Language panel (see specs/hebrew-greek-word-study.md). Old saved notes
  // with insight_lens='greek' stay readable; no migration needed since the
  // value is just a text label.
  CROSS_REFS: 'cross_refs',
  THEOLOGY: 'theology',
  APPLICATION: 'application',
  CUSTOM: 'custom',
}

/** Max character length for a custom "Ask your own" lens question. */
export const INSIGHT_CUSTOM_MAX_CHARS = 500

/** Soft cap for "Continue in Chat" pre-fill. Over this, degrade to a short summary. */
export const INSIGHT_CHAT_HANDOFF_MAX_CHARS = 4000

// ── Bible Reader defaults ───────────────────────────────────────
// Initial values for the Bible Navigation context when a user hasn't
// persisted their own last-read book/chapter/translation or font prefs.

/** Default Bible book for a first-time reader (the most accessible Gospel). */
export const DEFAULT_BIBLE_BOOK = 'John'

/** Default translation when the profile has none. Public-domain KJV is the
 *  safe fallback (covers both testaments, no licensing constraints). */
export const DEFAULT_BIBLE_TRANSLATION = 'kjv'

/** Default reader font size key (maps to FONT_SIZES in bibleBooks.js). */
export const DEFAULT_FONT_SIZE = 'medium'

/** Default reader font family key (maps to FONT_FAMILIES in bibleBooks.js). */
export const DEFAULT_FONT_FAMILY = 'serif'

// ── Learn ───────────────────────────────────────────────────────
/** Minimum reflection length (chars) that earns the Reflected mastery state. */
export const REFLECTION_MIN_CHARS = 30

/** Pass threshold (0–1) for the path-final exam. 80% correct earns Mastered. */
export const FINAL_EXAM_PASS_THRESHOLD = 0.8

// ── Chat history search ────────────────────────────────────────
/** Hard cap on chat history search results returned to the UI (mirrors the
 *  RPC's LIMIT 30; kept in JS so callers can slice defensively too). */
export const CHAT_SEARCH_RESULT_LIMIT = 30

// ── Share links ───────────────────────────────────────────────
/** Length of the random share token. 24 chars over the 56-symbol alphabet
 *  below ≈ 139 bits of entropy — infeasible to brute-force. */
export const SHARE_TOKEN_LENGTH = 24

/** URL-safe alphabet for share tokens. a-z A-Z 0-9 minus the visually
 *  ambiguous characters (0, O, 1, l, I) so pasted links stay readable. */
export const SHARE_TOKEN_ALPHABET = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789'
